<template>
  <v-container fluid class="access-logs-container">
    <v-row>
      <v-col cols="12">
        <v-card class="logs-card">
          <v-card-title class="d-flex align-center pa-4">
            <v-icon size="24" color="primary" class="mr-2">mdi-login</v-icon>
            <span class="text-h5">Logs de Acesso</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewMode" color="primary" variant="outlined">
              <v-btn value="dashboard">
                <v-icon>mdi-chart-line</v-icon>
                Dashboard
              </v-btn>
              <v-btn value="recent">
                <v-icon>mdi-history</v-icon>
                Recent
              </v-btn>
              <v-btn value="sessions">
                <v-icon>mdi-account-group</v-icon>
                Sessões
              </v-btn>
            </v-btn-toggle>
          </v-card-title>

          <!-- Vista Dashboard -->
          <div v-if="viewMode === 'dashboard'">
            <v-card-text class="pa-4">
              <!-- Filtros -->
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-select
                    v-model="selectedDays"
                    :items="daysOptions"
                    label="Período"
                    variant="outlined"
                    density="compact"
                    @update:modelValue="loadStats"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    @click="loadStats"
                    :loading="loading"
                    block
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Atualizar
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Cards de Estatísticas -->
              <v-row class="mb-4">
                <v-col cols="12" sm="6" md="2">
                  <v-card class="stat-card" color="blue-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number blue--text text-h4">{{ stats.total_logins }}</div>
                      <div class="stat-label text-caption">Total Logins</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card class="stat-card" color="green-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number green--text text-h4">{{ stats.successful_logins }}</div>
                      <div class="stat-label text-caption">Sucessos</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card class="stat-card" color="red-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number red--text text-h4">{{ stats.failed_logins }}</div>
                      <div class="stat-label text-caption">Falhas</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card class="stat-card" color="purple-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number purple--text text-h4">{{ stats.unique_users }}</div>
                      <div class="stat-label text-caption">Usuários Únicos</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card class="stat-card" color="orange-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number orange--text text-h4">{{ stats.avg_session_minutes }}m</div>
                      <div class="stat-label text-caption">Sessão Média</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card class="stat-card" color="teal-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number teal--text text-h4">{{ stats.unique_ips }}</div>
                      <div class="stat-label text-caption">IPs Únicos</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Gráficos -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title class="text-h6">
                      <v-icon color="blue" class="mr-2">mdi-chart-pie</v-icon>
                      Dispositivos
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="6">
                          <div class="device-stat">
                            <v-icon color="blue" size="20">mdi-desktop-classic</v-icon>
                            <span class="ml-2">Desktop: {{ stats.desktop_logins }}</span>
                          </div>
                        </v-col>
                        <v-col cols="6">
                          <div class="device-stat">
                            <v-icon color="green" size="20">mdi-cellphone</v-icon>
                            <span class="ml-2">Mobile: {{ stats.mobile_logins }}</span>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title class="text-h6">
                      <v-icon color="orange" class="mr-2">mdi-account-star</v-icon>
                      Top Usuários
                    </v-card-title>
                    <v-card-text>
                      <v-list density="compact">
                        <v-list-item
                          v-for="user in (Array.isArray(topUsers) ? topUsers.slice(0, 5) : [])"
                          :key="user.user_id || user.id"
                        >
                          <template v-slot:prepend>
                            <v-icon color="primary" size="20">mdi-account</v-icon>
                          </template>
                          <v-list-item-title>{{ user.user_name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ user.total_access }} acessos</v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </div>

          <!-- Vista Acessos Recentes -->
          <div v-if="viewMode === 'recent'">
            <v-card-text class="pa-4">
              <!-- Filtros -->
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-select
                    v-model="recentLimit"
                    :items="limitOptions"
                    label="Quantidade"
                    variant="outlined"
                    density="compact"
                    @update:modelValue="loadRecent"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    @click="loadRecent"
                    :loading="loading"
                    block
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Carregar
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Tabela de Acessos -->
              <v-card>
                <v-card-text class="pa-0">
                  <v-data-table
                    :items="recentAccess"
                    :headers="accessHeaders"
                    :loading="loading"
                    density="compact"
                    items-per-page="25"
                    class="access-table"
                  >
                    <template v-slot:item.login_time="{ item }">
                      <span class="text-caption">{{ formatDateTime(item.login_time) }}</span>
                    </template>
                    
                    <template v-slot:item.access_type="{ item }">
                      <v-chip
                        :color="getAccessColor(item.access_type)"
                        size="x-small"
                        variant="flat"
                      >
                        {{ item.access_type }}
                      </v-chip>
                    </template>
                    
                    <template v-slot:item.success="{ item }">
                      <v-icon
                        :color="item.success ? 'green' : 'red'"
                        size="20"
                      >
                        {{ item.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                    </template>
                    
                    <template v-slot:item.device_type="{ item }">
                      <v-icon
                        :color="getDeviceColor(item.device_type)"
                        size="20"
                      >
                        {{ getDeviceIcon(item.device_type) }}
                      </v-icon>
                    </template>
                    
                    <template v-slot:item.formatted_duration="{ item }">
                      <span v-if="item.formatted_duration" class="text-caption">
                        {{ item.formatted_duration }}
                      </span>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-card-text>
          </div>

          <!-- Vista Sessões Ativas -->
          <div v-if="viewMode === 'sessions'">
            <v-card-text class="pa-4">
              <v-row class="mb-4">
                <v-col cols="12" md="6">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    @click="loadSessions"
                    :loading="loading"
                    block
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Atualizar Sessões
                  </v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <v-btn
                    color="orange"
                    variant="outlined"
                    @click="cleanSessions"
                    :loading="cleaning"
                    block
                  >
                    <v-icon left>mdi-broom</v-icon>
                    Limpar Expiradas
                  </v-btn>
                </v-col>
              </v-row>

              <v-card>
                <v-card-text class="pa-0">
                  <v-data-table
                    :items="activeSessions"
                    :headers="sessionHeaders"
                    :loading="loading"
                    density="compact"
                    items-per-page="25"
                    class="sessions-table"
                  >
                    <template v-slot:item.login_time="{ item }">
                      <span class="text-caption">{{ formatDateTime(item.login_time) }}</span>
                    </template>
                    
                    <template v-slot:item.last_activity="{ item }">
                      <span class="text-caption">{{ formatDateTime(item.last_activity) }}</span>
                    </template>
                    
                    <template v-slot:item.expires_at="{ item }">
                      <span class="text-caption">{{ formatDateTime(item.expires_at) }}</span>
                    </template>
                    
                    <template v-slot:item.device_type="{ item }">
                      <v-icon
                        :color="getDeviceColor(item.device_type)"
                        size="20"
                      >
                        {{ getDeviceIcon(item.device_type) }}
                      </v-icon>
                    </template>
                    
                    <template v-slot:item.is_active="{ item }">
                      <v-chip
                        :color="item.is_active ? 'green' : 'red'"
                        size="x-small"
                        variant="flat"
                      >
                        {{ item.is_active ? 'Ativa' : 'Inativa' }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-card-text>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'

export default {
  name: 'AccessLogs',
  setup() {
    const viewMode = ref('dashboard')
    const loading = ref(false)
    const cleaning = ref(false)
    const stats = ref({
      total_logins: 0,
      successful_logins: 0,
      failed_logins: 0,
      unique_users: 0,
      unique_ips: 0,
      avg_session_minutes: 0,
      mobile_logins: 0,
      desktop_logins: 0
    })
    const recentAccess = ref([])
    const activeSessions = ref([])
    const topUsers = ref([])
    
    // Filtros
    const selectedDays = ref(30)
    const recentLimit = ref(50)
    
    const daysOptions = [
      { title: 'Últimos 7 dias', value: 7 },
      { title: 'Últimos 30 dias', value: 30 },
      { title: 'Últimos 90 dias', value: 90 }
    ]
    
    const limitOptions = [
      { title: '25', value: 25 },
      { title: '50', value: 50 },
      { title: '100', value: 100 },
      { title: '200', value: 200 }
    ]
    
    const accessHeaders = [
      { title: 'Data/Hora', key: 'login_time', width: '150px' },
      { title: 'Usuário', key: 'user_name' },
      { title: 'Tipo', key: 'access_type', width: '100px' },
      { title: 'IP', key: 'ip_address', width: '120px' },
      { title: 'Dispositivo', key: 'device_type', width: '80px' },
      { title: 'Navegador', key: 'browser' },
      { title: 'Sucesso', key: 'success', width: '80px' },
      { title: 'Duração', key: 'formatted_duration', width: '100px' }
    ]
    
    const sessionHeaders = [
      { title: 'Usuário', key: 'user_name' },
      { title: 'IP', key: 'ip_address', width: '120px' },
      { title: 'Login', key: 'login_time', width: '150px' },
      { title: 'Última Atividade', key: 'last_activity', width: '150px' },
      { title: 'Expira', key: 'expires_at', width: '150px' },
      { title: 'Dispositivo', key: 'device_type', width: '80px' },
      { title: 'Status', key: 'is_active', width: '80px' }
    ]
    
    const loadStats = async () => {
      loading.value = true
      try {
        const [statsRes, topUsersRes] = await Promise.all([
          axios.get(`/api/access-stats-production.php?action=stats&days=${selectedDays.value}`),
          axios.get('/api/access-stats-production.php?action=top-users&limit=10')
        ])
        
        stats.value = statsRes.data
        topUsers.value = Array.isArray(topUsersRes.data) ? topUsersRes.data : []
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error)
        topUsers.value = [] // Garantir que seja array vazio em caso de erro
      } finally {
        loading.value = false
      }
    }
    
    const loadRecent = async () => {
      loading.value = true
      try {
        const response = await axios.get(`/api/access-stats-production.php?action=recent&limit=${recentLimit.value}`)
        recentAccess.value = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Erro ao carregar acessos recentes:', error)
        recentAccess.value = []
      } finally {
        loading.value = false
      }
    }
    
    const loadSessions = async () => {
      loading.value = true
      try {
        const response = await axios.get('/api/access-stats-production.php?action=active-sessions')
        activeSessions.value = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Erro ao carregar sessões ativas:', error)
        activeSessions.value = []
      } finally {
        loading.value = false
      }
    }
    
    const cleanSessions = async () => {
      if (!confirm('Tem certeza que deseja limpar sessões expiradas?')) return
      
      cleaning.value = true
      try {
        const response = await axios.post('/api/access-stats-production.php?action=clean-sessions')
        alert(`${response.data.cleaned} sessões limpas!`)
        loadSessions()
      } catch (error) {
        console.error('Erro ao limpar sessões:', error)
        alert('Erro ao limpar sessões')
      } finally {
        cleaning.value = false
      }
    }
    
    const formatDateTime = (timestamp) => {
      return new Date(timestamp).toLocaleString('pt-BR')
    }
    
    const getAccessColor = (type) => {
      switch (type) {
        case 'LOGIN': return 'green'
        case 'LOGOUT': return 'blue'
        case 'FAILED_LOGIN': return 'red'
        default: return 'grey'
      }
    }
    
    const getDeviceColor = (type) => {
      switch (type) {
        case 'DESKTOP': return 'blue'
        case 'MOBILE': return 'green'
        case 'TABLET': return 'orange'
        default: return 'grey'
      }
    }
    
    const getDeviceIcon = (type) => {
      switch (type) {
        case 'DESKTOP': return 'mdi-desktop-classic'
        case 'MOBILE': return 'mdi-cellphone'
        case 'TABLET': return 'mdi-tablet'
        default: return 'mdi-help'
      }
    }
    
    onMounted(() => {
      loadStats()
      loadRecent()
      loadSessions()
    })
    
    return {
      viewMode,
      loading,
      cleaning,
      stats,
      recentAccess,
      activeSessions,
      topUsers,
      selectedDays,
      recentLimit,
      daysOptions,
      limitOptions,
      accessHeaders,
      sessionHeaders,
      loadStats,
      loadRecent,
      loadSessions,
      cleanSessions,
      formatDateTime,
      getAccessColor,
      getDeviceColor,
      getDeviceIcon
    }
  }
}
</script>

<style scoped>
.access-logs-container {
  background: #f5f5f5;
  min-height: 100vh;
}

.logs-card {
  margin: 0;
}

.stat-card {
  height: 100px;
}

.stat-number {
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.device-stat {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.access-table,
.sessions-table {
  font-size: 12px;
}
</style>
